/* global $ */
const videojs = require('video.js');
require('video.js/dist/video-js.css');

const template = require('./templates/video.handlebars');
const videoTemplate = require('./templates/glencoe-video.handlebars');

class Hippio {
    static get METADATA_BASEURI() {
        return '//movie-usa.glencoesoftware.com/metadata/';
    }

    static get FOUR_BY_THREE() {
        return 4.0 / 3.0;
    }

    static get SIXTEEN_BY_NINE() {
        return 16.0 / 9.0;
    }

    static get MAX_HEIGHT() {
        return 600;
    }

    static get MIN_WIDTH() {
        return 300;
    }

    static get VIDEO_ELEMENT_SELECTOR() {
        return '#ContentTab p';
    }

    constructor() {
        this.doi = $('meta[name="citation_doi"]')
            .first()
            .attr('content');
        this.isSplitScreen = $('body').hasClass('pg_articlesplitview');
    }

    getUrlParameterValue(name) {
        const urlParamsStr = window.location.search.substring(1);
        const variables = urlParamsStr.split('&');
        for (const value of variables) {
            const key = value.split('=')[0];
            if (key === name) {
                return value.split('=')[1];
            }
        }
        return '';
    }

    init() {
        if (!this.doi) {
            return;
        }

        $.ajax({
            url: Hippio.METADATA_BASEURI + this.doi,
        }).done((data) => {
            this.data = data;
            this.dataByElementId = {};
            this.nameByVideoId = {};
            for (const key of Object.keys(this.data)) {
                const video = this.data[key];
                if (video.id) {
                    this.dataByElementId[video.id] = video;
                }
                this.nameByVideoId[video.video_id] = key;
            }
            this.inlined = {};
            if (!this.getUrlParameterValue('no-injection')) {
                this.inject();
            }
        });
    }

    getAllVideoElements() {
        return $(Hippio.VIDEO_ELEMENT_SELECTOR).filter((index, element) => (
            (/Video|Movie/i).test($(element).text())
        ));
    }

    getAllVideoNamesFromElement(element) {
        return this.getAllVideoNamesFromText($(element).text());
    }

    capitalize(x) {
        return x.charAt(0).toUpperCase() + x.slice(1);
    }

    getVideoNamesDashed(baseMatch) {
        // 'Video ' or 'Movie '
        const prefix = `${baseMatch.substring(0, 5)} `;
        const matches = new Set();
        const pattern = /(S?)(\d+)[–-]S?(\d+)/;
        const keys = pattern.exec(baseMatch);
        if (!keys || keys.length !== 4) {
            return matches;
        }
        const start = parseInt(keys[2], 10);
        const end = parseInt(keys[3], 10);
        for (let i = start; i <= end; i += 1) {
            matches.add(prefix + keys[1] + i);
        }
        return matches;
    }

    getVideoNamesCommasAndNormal(baseMatch) {
        // ' Video ' or 'Movie '
        const prefix = `${baseMatch.substring(0, 5)} `;
        const matches = new Set();
        const pattern = /(S?\d+[a-zA-Z]*),?/g;
        let number = pattern.exec(baseMatch);
        while (number) {
            // remove leading zeros in number
            matches.add(prefix + number[1].replace(/(S?)0+([1-9]+)/, '$1$2'));
            number = pattern.exec(baseMatch);
        }
        return matches;
    }

    getAllVideoNamesFromText(content) {
        let matches = new Set();
        if (!content) {
            return matches;
        }
        const pattern = new RegExp([
            '(Video|Movie)s?\\s*(',
            '(S?\\d+[–\\-]+S?\\d+)', '|',
            '(S?\\d+[a-zA-Z]*)',
            ',?\\s*)+',
            '(and\\s+(S?\\d+[a-zA-Z]*),?\\s*)*',
        ].join(''), 'ig');
        // replace to match "Video 1 to 5" with existing regex
        let text = content.replace(/\s+to\s+/g, '-');
        text = text.replace('_', ' '); // for names like "Movie_S01"
        const baseMatches = text.match(pattern);
        if (baseMatches) {
            for (const baseMatch of baseMatches) {
                matches = new Set([
                    ...matches,
                    ...this.getVideoNamesDashed(baseMatch),
                    ...this.getVideoNamesCommasAndNormal(baseMatch),
                ]);
            }
        }
        return [...matches].map((x) => this.capitalize(x));
    }

    getVideoKeyByName(name) {
        return name.replace('Movie', 'Video').replace(/ [S0]/, ' ');
    }

    getVideoNumberAndSuffix(name) {
        const match = /(\d+)([a-zA-Z]*)/.exec(name);
        const getStruct = (number, suffix) => ({ number, suffix });
        return match
            ? getStruct(parseInt(match[1], 10), match[2]) : getStruct(-1, '');
    }

    static compareVideoNumberAndSuffix(a, b) {
        if (a.number < b.number) {
            return -1;
        }
        if (a.number > b.number) {
            return 1;
        }
        if (a.suffix < b.suffix) {
            return -1;
        }
        if (a.suffix > b.suffix) {
            return 1;
        }
        return 0;
    }

    getAppendableElement(element, name) {
        // every "block", text or figure is enclosed by an article section
        // wrapper
        let appendable = $(element).parents('div.article-section-wrapper');
        const number = this.getVideoNumberAndSuffix(name);
        // insert after any figures that are not a larger numbered video
        let next = appendable.next('div.article-section-wrapper:has(div.fig)');
        let nextNumber = this.getVideoNumberAndSuffix(
            next.children('div.vid').attr('data-name'),
        );
        while (
            next.length > 0
            && (number.number === -1
                || Hippio.compareVideoNumberAndSuffix(number, nextNumber) > 0
            )
        ) {
            appendable = next;
            next = appendable.next('div.article-section-wrapper:has(div.fig)');
            nextNumber = this.getVideoNumberAndSuffix(
                next.children('div.vid').attr('data-name'),
            );
        }
        return appendable;
    }

    expandData(name, data) {
        if (!data) {
            return data;
        }
        if (!data.title.endsWith('.')) {
            data.title += '.';
        }
        const aspectRatio = data.width / data.height;
        let aspectRatioClass = 'vjs-16-9';
        if (Math.abs(aspectRatio - Hippio.FOUR_BY_THREE)
            < Math.abs(aspectRatio - Hippio.SIXTEEN_BY_NINE)) {
            aspectRatioClass = 'vjs-4-3';
        }
        data.aspect_ratio_str = `${data.width}:${data.height}`;
        data.aspect_ratio_class = aspectRatioClass;
        data.name = name;
        data.is_split_screen = this.isSplitScreen;
        data.video_element_id = `${data.video_id}-player`;

        let maxWidth = data.width;
        if (data.height > Hippio.MAX_HEIGHT) {
            maxWidth = parseInt(
                (data.width * Hippio.MAX_HEIGHT) / data.height, 10,
            );
        }
        if (maxWidth < Hippio.MIN_WIDTH) {
            maxWidth = Hippio.MIN_WIDTH;
        }
        data.max_width = maxWidth;
        return data;
    }

    hippiofyNewMode(elements) {
        // standard mode
        elements.each((index, element) => {
            element = $(element);
            const elementId = element.attr('data-id');
            let data = this.dataByElementId[elementId];
            if (!data) {
                return;
            }
            const name = this.nameByVideoId[data.video_id];
            if (!name) {
                return;
            }
            data = this.expandData(name, data);
            const toReplace = element.find('span.glencoe-video-stub');
            this.inlined[name] = true;
            toReplace.replaceWith(videoTemplate(data));
            videojs(
                data.video_element_id,
                { aspectRatio: data.aspect_ratio_str },
            );
            element.find('.glencoe-video-newtab')
                .attr('href', data.solo_href);
            element.find('.glencoe-video-download')
                .attr('href', data.source_href);
        });
    }

    hippiofy(elements) {
        // split screen mode
        if (this.isSplitScreen) {
            const prependTo = $(
                'div.split-view-article-figures-and-tables-figures '
                + 'div.widget-ArticleFiguresAndTables',
            );
            const entries = Object.entries(this.data)
                .sort((a, b) => (
                    Hippio.compareVideoNumberAndSuffix(
                        this.getVideoNumberAndSuffix(a[0]),
                        this.getVideoNumberAndSuffix(b[0]),
                    )
                ));
            entries.reverse();
            for (const [key, data] of entries) {
                this.inlined[key] = true;
                $(template(this.expandData(data.video_id, data)))
                    .prependTo(prependTo);
                videojs(
                    data.video_element_id,
                    { aspectRatio: data.aspect_ratio_str },
                );
            }
            return;
        }

        // standard mode
        elements.each((index, element) => {
            const names = this.getAllVideoNamesFromElement(element);
            console.log(
                'Processing element ', element, ' containing names ', names,
            );
            for (const name of names) {
                const key = this.getVideoKeyByName(name);
                const data = this.expandData(name, this.data[key]);

                if (key && data && !this.inlined[key]) {
                    const appendTo = this.getAppendableElement(element, name);
                    this.inlined[key] = true;
                    appendTo.after(template(data));
                    videojs(
                        data.video_element_id,
                        { aspectRatio: data.aspect_ratio_str },
                    );
                } else {
                    console.log(
                        'Could not collect all necessary information ',
                        'or already inlined. name=', name, ' key=',
                        key, ' data=', data,
                    );
                }
            }
        });
    }

    inject() {
        if ($('.glencoe-video').length > 0) {
            const elements = $('div.glencoe-video');
            this.hippiofyNewMode(elements);
        } else {
            const elements = this.isSplitScreen
                ? $() : this.getAllVideoElements().filter(':visible');
            this.hippiofy(elements);
        }
        $(document).trigger('hippio-videos-created');
    }
}

const hippio = new Hippio();
hippio.init();

export default hippio;
